import { useGetCoreLocationGroupsInfiniteQuery } from '@autone/redux/core';
import { sentenceCase } from '@autone/utils';
import { forwardRef } from 'react';

import { VirtualMultiselect } from '../../virtual-multiselect';
import { CoreGeneralFilter } from '../components/CoreGeneralFilter';
import { SERVER_SIDE_SEARCH_LIMIT } from '../constants';
import type {
  CoreGeneralFilterProps,
  CoreGeneralFilterRef,
} from '../CoreGeneralFilters.types';
import { useCoreLocationGeneralFiltersContext } from '../hooks/useCoreLocationGeneralFiltersContext';

export const CoreLocationGroupFilter = forwardRef<
  CoreGeneralFilterRef,
  CoreGeneralFilterProps
>(({ alwaysCommit, searchTerm, className }, ref) => {
  const {
    dimensionAttributeValue,
    setDimensionAttributeValue,
    coreLocationFiltersQueryArg,
  } = useCoreLocationGeneralFiltersContext('location_groups.groups');

  const { items, isFetching, isFetchingNextPage, fetchNextPage } =
    useGetCoreLocationGroupsInfiniteQuery(
      {
        query: {
          limit: SERVER_SIDE_SEARCH_LIMIT,
          search_term: searchTerm,
        },
        body: {
          filters: {
            geographic: coreLocationFiltersQueryArg.geographic ?? {},
            location_cluster:
              coreLocationFiltersQueryArg.location_cluster ?? {},
            location_groups: coreLocationFiltersQueryArg.location_groups ?? {},
          },
        },
      },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            items: data?.pages.flatMap((page) => page.items) ?? [],
          };
        },
      },
    );

  return (
    <CoreGeneralFilter
      isFetching={isFetching && !isFetchingNextPage}
      onScrollEnd={fetchNextPage}
      ref={ref}
      selectedItems={dimensionAttributeValue}
      onSelectedItemsChange={setDimensionAttributeValue}
      alwaysCommit={alwaysCommit}
      className={className}
      items={items}
      renderItem={(item) => (
        <VirtualMultiselect.Item>
          <VirtualMultiselect.Checkbox />
          {sentenceCase(item.payload.group)}
        </VirtualMultiselect.Item>
      )}
    />
  );
});

CoreLocationGroupFilter.displayName = 'CoreLocationGroupFilter';
